import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Plus } from 'lucide-react';
import { getLessons, getUnits, updateLesson } from '../firebase/lessonPlannerService';
import { getAssignments } from '../firebase/assignmentService';
import DashboardLayout from './DashboardLayout';
import './PlannerCalendar.css';

function PlannerCalendar() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [lessons, setLessons] = useState([]);
  const [units, setUnits] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [showLessonModal, setShowLessonModal] = useState(false);

  useEffect(() => {
    if (!currentUser) {
      navigate('/');
      return;
    }
    loadData();
  }, [currentUser, navigate]);

  const loadData = async () => {
    setLoading(true);
    const [lessonsResult, unitsResult, assignmentsResult] = await Promise.all([
      getLessons(currentUser.id),
      getUnits(currentUser.id),
      getAssignments(currentUser.uid)
    ]);

    if (lessonsResult.success) {
      setLessons(lessonsResult.lessons);
    }
    if (unitsResult.success) {
      setUnits(unitsResult.units);
    }
    if (assignmentsResult.success) {
      setAssignments(assignmentsResult.assignments);
    }
    setLoading(false);
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];

    // Add empty cells for days before the first of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }

    return days;
  };

  const getLessonsForDate = (date) => {
    if (!date) return [];

    return lessons.filter(lesson => {
      // Check both scheduledDate and startDate
      const dateField = lesson.startDate || lesson.scheduledDate;
      if (!dateField) return false;

      const lessonDate = typeof dateField === 'string'
        ? parseDateLocal(dateField)
        : dateField.toDate
        ? dateField.toDate()
        : new Date(dateField);

      return lessonDate.toDateString() === date.toDateString();
    });
  };

  const getAssignmentsForDate = (date) => {
    if (!date) return [];

    return assignments.filter(assignment => {
      if (!assignment.assignmentDate) return false;

      const assignDate = typeof assignment.assignmentDate === 'string'
        ? parseDateLocal(assignment.assignmentDate)
        : assignment.assignmentDate.toDate
        ? assignment.assignmentDate.toDate()
        : new Date(assignment.assignmentDate);

      return assignDate.toDateString() === date.toDateString();
    });
  };

  const getUnitTitle = (unitId) => {
    const unit = units.find(u => u.id === unitId);
    return unit?.title || 'No Unit';
  };

  const previousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));
  };

  const isToday = (date) => {
    if (!date) return false;
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const formatMonthYear = (date) => {
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  // Helper function to format date as YYYY-MM-DD in local timezone
  const formatDateLocal = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Helper function to parse YYYY-MM-DD string as local date
  const parseDateLocal = (dateString) => {
    const [year, month, day] = dateString.split('-').map(Number);
    return new Date(year, month - 1, day);
  };

  const handleDragStart = (e, lesson) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('lessonId', lesson.id);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    if (e.currentTarget.contains(e.relatedTarget)) return;
    e.currentTarget.classList.add('drag-over');
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    if (e.currentTarget.contains(e.relatedTarget)) return;
    e.currentTarget.classList.remove('drag-over');
  };

  const handleDrop = async (e, targetDate) => {
    e.preventDefault();
    e.currentTarget.classList.remove('drag-over');
    const lessonId = e.dataTransfer.getData('lessonId');

    if (!lessonId || !targetDate) return;

    const lesson = lessons.find(l => l.id === lessonId);
    if (!lesson) return;

    // Format the date as YYYY-MM-DD in local timezone
    const formattedDate = formatDateLocal(targetDate);

    // Update the lesson with the new date
    const updateData = {};
    if (lesson.startDate) {
      updateData.startDate = formattedDate;
      // If it's a date range lesson, keep the duration
      if (lesson.endDate) {
        // Calculate the duration in days
        const oldStart = new Date(lesson.startDate);
        const oldEnd = new Date(lesson.endDate);
        const durationDays = Math.floor((oldEnd - oldStart) / (1000 * 60 * 60 * 24));

        // Set the new end date based on the duration
        const newEnd = new Date(targetDate);
        newEnd.setDate(newEnd.getDate() + durationDays);
        updateData.endDate = formatDateLocal(newEnd);
      }
    } else if (lesson.scheduledDate) {
      updateData.scheduledDate = formattedDate;
    }

    const result = await updateLesson(lessonId, updateData);

    if (result.success) {
      // Reload lessons to reflect the change
      await loadData();
    } else {
      alert('Failed to update lesson date');
    }
  };

  const days = getDaysInMonth(currentDate);
  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  if (loading) {
    return (
      <DashboardLayout>
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading calendar...</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="planner-calendar-container">
        <div className="calendar-header">
          <div>
            <h1>Curriculum Calendar</h1>
            <p className="subtitle">View and manage your lesson schedule</p>
          </div>
        </div>

        <div className="calendar-controls">
          <button onClick={previousMonth} className="month-nav-btn">
            <ChevronLeft size={20} />
          </button>
          <h2 className="month-title">{formatMonthYear(currentDate)}</h2>
          <button onClick={nextMonth} className="month-nav-btn">
            <ChevronRight size={20} />
          </button>
        </div>

        <div className="calendar-grid-wrapper">
          <div className="calendar-weekdays">
            {weekDays.map(day => (
              <div key={day} className="weekday-header">
                {day}
              </div>
            ))}
          </div>

          <div className="calendar-days-grid">
            {days.map((date, index) => {
              const dayLessons = date ? getLessonsForDate(date) : [];
              const dayAssignments = date ? getAssignmentsForDate(date) : [];
              const totalItems = dayLessons.length + dayAssignments.length;

              return (
                <div
                  key={index}
                  className={`calendar-day ${!date ? 'empty' : ''} ${isToday(date) ? 'today' : ''}`}
                  onClick={() => date && setSelectedDate(date)}
                  onDragOver={date ? handleDragOver : undefined}
                  onDragEnter={date ? handleDragEnter : undefined}
                  onDragLeave={date ? handleDragLeave : undefined}
                  onDrop={date ? (e) => handleDrop(e, date) : undefined}
                >
                  {date && (
                    <>
                      <div className="day-number">{date.getDate()}</div>
                      <div className="day-lessons">
                        {dayLessons.slice(0, 2).map(lesson => (
                          <div
                            key={lesson.id}
                            className="lesson-pill"
                            title={lesson.title}
                            draggable={true}
                            onDragStart={(e) => handleDragStart(e, lesson)}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <span className="lesson-title">📚 {lesson.title}</span>
                            {lesson.unitId && (
                              <span className="lesson-unit">{getUnitTitle(lesson.unitId)}</span>
                            )}
                          </div>
                        ))}
                        {dayAssignments.slice(0, 2).map(assignment => (
                          <div
                            key={assignment.id}
                            className="lesson-pill assignment-pill"
                            title={assignment.title}
                            style={{ backgroundColor: '#fef3c7', borderLeft: '3px solid #f59e0b' }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <span className="lesson-title">📝 {assignment.title}</span>
                          </div>
                        ))}
                        {totalItems > 4 && (
                          <div className="more-lessons">
                            +{totalItems - 4} more
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {selectedDate && (
          <div className="modal-overlay" onClick={() => setSelectedDate(null)}>
            <div className="modal-content day-details-modal" onClick={(e) => e.stopPropagation()}>
              <h2>{selectedDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}</h2>

              <div className="day-lessons-list">
                {getLessonsForDate(selectedDate).length > 0 && (
                  <>
                    <h3 style={{ marginTop: 0 }}>📚 Lessons</h3>
                    {getLessonsForDate(selectedDate).map(lesson => (
                      <div key={lesson.id} className="lesson-detail-card">
                        <h4>{lesson.title}</h4>
                        {lesson.description && <p className="lesson-description">{lesson.description}</p>}
                        <div className="lesson-meta">
                          {lesson.unitId && (
                            <span className="meta-tag">{getUnitTitle(lesson.unitId)}</span>
                          )}
                          {lesson.duration && (
                            <span className="meta-tag">{lesson.duration} min</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </>
                )}

                {getAssignmentsForDate(selectedDate).length > 0 && (
                  <>
                    <h3 style={{ marginTop: getLessonsForDate(selectedDate).length > 0 ? '1.5rem' : 0 }}>📝 Assignments</h3>
                    {getAssignmentsForDate(selectedDate).map(assignment => (
                      <div key={assignment.id} className="lesson-detail-card" style={{ borderLeft: '3px solid #f59e0b' }}>
                        <h4>{assignment.title}</h4>
                        {assignment.description && <p className="lesson-description">{assignment.description}</p>}
                        <div className="lesson-meta">
                          {assignment.classNames && assignment.classNames.length > 0 && (
                            <span className="meta-tag">{assignment.classNames.join(', ')}</span>
                          )}
                          {assignment.dueDate && (
                            <span className="meta-tag" style={{ backgroundColor: '#fecaca', color: '#991b1b' }}>
                              Due: {new Date(assignment.dueDate.toDate ? assignment.dueDate.toDate() : assignment.dueDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </>
                )}

                {getLessonsForDate(selectedDate).length === 0 && getAssignmentsForDate(selectedDate).length === 0 && (
                  <div className="no-lessons">
                    <p>No lessons or assignments scheduled for this day</p>
                  </div>
                )}
              </div>

              <div className="modal-actions">
                <button onClick={() => setSelectedDate(null)} className="btn-secondary">
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

export default PlannerCalendar;
